import { useNavigate } from 'react-router'
import { getConsignmentsUrlWithCurrentParams } from '~/entities/consignment'
import { getDocumentsUrl } from '~/entities/document'
import { pluralize } from '~/shared/lib/pluralize'
import { Button, Dialog, OptiWalking, ScrollArea, Text } from '~/shared/ui'
import type { DocumentPageRange } from '../../lib'
import { getDisplayDocumentId } from '../../lib'

export function SaveAndSplitDialog({
  consignmentKey,
  documentPageRanges,
  onClose,
  onSubmit,
  isSubmitting,
  isSubmitted,
}: {
  consignmentKey: string
  documentPageRanges: DocumentPageRange[]
  onClose: () => void
  onSubmit: () => void
  isSubmitting: boolean
  isSubmitted: boolean
}) {
  const navigate = useNavigate()

  const close = () => {
    if (!isSubmitting) {
      onClose()
    }
  }

  return (
    <Dialog
      size={isSubmitting || isSubmitted ? 'md' : 'sm'}
      onClose={close}
      actions={
        isSubmitting || isSubmitted ? (
          <>
            <Button
              onClick={close}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              raised
              tonal
              onClick={() => navigate(getConsignmentsUrlWithCurrentParams())}
              disabled={isSubmitting}
            >
              Review consignments
            </Button>
            <Button
              raised
              onClick={() => navigate(getDocumentsUrl({ assetId: consignmentKey }))}
              disabled={isSubmitting}
            >
              Review docs
            </Button>
          </>
        ) : (
          <>
            <Button onClick={close}>Cancel</Button>
            <Button
              raised
              onClick={onSubmit}
            >
              Save and split
            </Button>
          </>
        )
      }
    >
      {isSubmitting || isSubmitted ? (
        <div className="flex flex-col gap-4">
          <OptiWalking animated={isSubmitting} />
          <div className="flex flex-col gap-2">
            <Text
              className="text-text-primary"
              appearance="SUBTITLE2"
            >
              Documents Ready for Review
            </Text>
            <Text
              className="text-text-secondary"
              appearance="CAPTION"
            >
              The consignment is being split and will be ready for review.
            </Text>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <Text
            className="text-text-primary"
            appearance="SUBTITLE2"
          >
            Confirm your split
          </Text>
          <Text
            className="text-text-secondary"
            appearance="CAPTION"
          >
            opti will split the consignment <span className="uppercase">{consignmentKey}</span> into{' '}
            {documentPageRanges.length} {pluralize(documentPageRanges.length, 'document')}:
          </Text>
          <ScrollArea viewportClassName="max-h-[50vh]">
            <div className="flex flex-col gap-2">
              {documentPageRanges.map((documentPageRange, index) => {
                const pageCount =
                  documentPageRange.pageRange.endPage - documentPageRange.pageRange.startPage + 1
                return (
                  <Text
                    key={documentPageRange.documentId}
                    appearance="CAPTION"
                  >
                    <span className="text-text-primary">Document {index + 1}</span>{' '}
                    <span>
                      (
                      <span className="uppercase">
                        {getDisplayDocumentId(documentPageRange.documentId)}
                      </span>
                      ) – {pageCount} {pluralize(pageCount, 'page')}
                    </span>
                  </Text>
                )
              })}
            </div>
          </ScrollArea>
        </div>
      )}
    </Dialog>
  )
}
